import { getCookie } from '@alexis/helpers/cookie';
import { gtmTrack } from '@alexis/helpers/gtm';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { groupTripViewModelsBasedOnDepartLeg } from '@pages/FlightSearchResult/flightPerLegSearch';
import { filterTripViewModelsBasedOnOneLeg } from '@pages/FlightSearchResult/flightPerLegSearchFilter';
import { sortDepartLegGroups } from '@pages/FlightSearchResult/flightPerLegSearchSort';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import { DepartLegGroup } from '@pages/FlightSearchResult/types';
import { groupBy, uniqBy } from 'lodash-es';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { getRealAdPosition } from '@components/flights/FlightSearchResultContentAds/helper';

import { EXCLUDED_PROVIDERS_PARAM, IS_OFFER_ENGINE_ENABLED_PARAM } from '@constants/flight';
import { GTM_ID } from '@constants/general';

import { useFlightContext } from '@context/FlightContext';

import { convertListToHashMap, notUndefinedAndNull } from '@helpers/array';
import {
  getOfferEngineIntegrationAbTestVariant,
  groupTripViewModelBasedOnOutboundAirlineCodeAndPriceMap,
  mergeMinMax,
} from '@helpers/flight';
import {
  filterOnewayAndRoundTripViewModelBasedOnFilters,
  filterMultiCityTripViewModelBasedOnFilters,
} from '@helpers/flights/flightFilters';
import { sortFlightFares, sortTripViewModels } from '@helpers/flightSort';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { flightSearchObservable } from '@observables/flightObservables';

import { flightSearchResultObserver } from '@observers/flightObserver';

import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getLocaleState,
  getPriceOptionsState,
  getUserState,
} from '@redux/selectors';

import { AdPositionConfig } from '@wegoTypes/adPositionConfig';
import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';
import { FlightSearchResult } from '@wegoTypes/flights/metasearch/flightSearchResult';
import { FlightSearchResultSearch } from '@wegoTypes/flights/metasearch/flightSearchResultSearch';
import { SegmentViewModel } from '@wegoTypes/flights/segmentViewModel';
import { GroupedTripViewModel, TripViewModel } from '@wegoTypes/flights/tripViewModel';

import { FlightSearchFiltersContext } from '../FlightSearchFiltersContext';

type FlightSearchResultsProviderValue = {
  search: FlightSearchResultSearch;
  isLoading: boolean;
  pollIndex: number;
  flightMetaSearchError: { message: string; status: string };
  flightMetaSearchOriginalError: Error | undefined;
  showTurnstileWidget: boolean;
  progressPercent: number;
  airlineFilterOptions: FlightMetaSearchFilterOption[];
  allianceFilterOptions: FlightMetaSearchFilterOption[];
  destinationAirportFilterOptions: FlightMetaSearchFilterOption[];
  originAirportFilterOptions: FlightMetaSearchFilterOption[];
  stopFilterOptions: FlightMetaSearchFilterOption[];
  stopoverAirportFilterOptions: FlightMetaSearchFilterOption[];
  legFilterOptions: FlightMetaSearchFilterOption[];
  stopoverDurationFilterOptions: NumberRange;
  tripViewModels: TripViewModel[];
  sponsoredAdTripViewModels: TripViewModel[];
  exchangeRates: ExchangeRate[];
  exchangeRate: ExchangeRate;
  providerFilterOptions: FlightSearchResultProvider[];
  expandedGroupKeys: string[];
  setExpandedGroupKeys: React.Dispatch<React.SetStateAction<string[]>>;
  setLeaderboardAdsAndInlineAdsConfig: (config: AdPositionConfig[]) => void;
  groupedTripViewModelDetailWithoutPlaceholder: TripViewModel[] | GroupedTripViewModel[];
  finalTripViewModelsToShowBasedOnSortType: TripViewModel[];
  sortedAndFilteredTripViewModelsWithoutPlaceholder: TripViewModel[];
  filteredSponsoredAdTripViewModels: TripViewModel[];
  groupedTripViewModelsDetail: GroupedTripViewModel[];
  sortedAndFilteredTripViewModels: TripViewModel[];
  sortedAndFilteredTripViewModelsForRecommendedSortFlight2: TripViewModel[];
  sortedAndFilteredTripViewModelsForPerLegSearch: TripViewModel[];
  cheapestFareViewModel?: FareViewModel;
  bestExperienceCheapestFareViewModel?: FareViewModel;
  cheapestFare?: Price;
  fastestCheapestFare?: Price;
  bestExperienceCheapestFare?: Price;
  aggregatedFlightSearchResult: FlightSearchResult;
  multiCityActiveLeg: FlightMetaSearchFilterOption;
  allLegsAirlinesFilterOptions: FlightMetaSearchFilterOption[];
  allLegsStopoverAirportFilterOptions: FlightMetaSearchFilterOption[];
  minUsdPrice: number;
  maxUsdPrice: number;
  usdPrices: number[];
  hasRenderedResults: boolean;
  setHasRenderedResults: React.Dispatch<React.SetStateAction<boolean>>;
  isRenderingFirstResults: boolean;
  offerEngineIntegrationAbTestVariant: string;
  isPollFailingMidway: boolean;
};

const FlightSearchResultsContext = createContext<FlightSearchResultsProviderValue>(
  null as unknown as FlightSearchResultsProviderValue,
);

export type FlightSearchResultsProviderProps = {
  children: React.ReactNode;
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  gtmAuth: string;
  gtmPreview: string;
};

const EMPTY_FLIGHT_SEARCH_RESULT: FlightSearchResult = {
  airlines: [],
  airports: [],
  cities: [],
  fares: [],
  filters: {
    airlines: [],
    alliances: [],
    destinationAirports: [],
    legs: [],
    originAirports: [],
    stopoverAirports: [],
    stopoverDurations: null as any,
    stops: [],
  },
  legs: [],
  paymentMethods: [],
  providers: [],
  search: null as any,
  trips: [],
  sponsors: [],
  currencies: [],
  count: 0,
  scores: {},
};

const FlightSearchResultsProvider = ({
  children,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  gtmAuth,
  gtmPreview,
}: FlightSearchResultsProviderProps) => {
  const { isFlightSearch2AndNotMulticity, isCacheFlightSearch } = useFlightContext();

  const [showTurnstileWidget, setShowTurnstileWidget] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>([]);
  const [hasRenderedResults, setHasRenderedResults] = useState(false);

  const [aggregatedFlightSearchResult, setAggregatedFlightSearchResult] =
    useState<FlightSearchResult>(EMPTY_FLIGHT_SEARCH_RESULT);
  const [isLoading, setIsLoading] = useState(true);
  const [pollIndex, setPollIndex] = useState(0);
  const [flightMetaSearchError, setFlightMetaSearchError] = useState<{
    message: string;
    status: string;
  }>({
    message: '',
    status: '',
  });
  const [flightMetaSearchOriginalError, setFlightMetaSearchOriginalError] = useState<Error>();

  // Escape hatch to circular dependencies between this context and useFlightSearchAds
  // DO NOT use this state elsewhere
  const [leaderboardAdsAndInlineAdsConfig, setLeaderboardAdsAndInlineAdsConfig] = useState<
    AdPositionConfig[]
  >([]);

  const {
    adultCount,
    cabinClass,
    childCount,
    infantCount,
    legs,
    paymentMethods,
    passengerCount,
    isMultiCity,
  } = useFlightSearchPageParams();

  const { currentlySelectedLegIdPLS, isPerLegSearch, isPLSDepartLegSelection } = useFlightPLS();

  const {
    airlineCodesFilter,
    allianceCodesFilter,
    departDurationsFilter,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    destinationAirportCodesFilter,
    flightExperiencesFilter,
    originAirportCodesFilter,
    paymentMethodIdsFilter,
    pricesFilter,
    providerCodesFilter,
    providersFilter,
    returnDurationsFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    stopCodesFilter,
    stopoverAirportCodesFilter,
    stopoverDurationsFilter,
    stopoverOptionsFilter,
    tripsFilter,
    fallbackFilter,
    activeLeg,
    selectedLegs,
  } = useContext(FlightSearchFiltersContext);

  const { sortType, sortOrder } = useFlightSearchSort();

  const allianceCodesFilterForActiveLeg = useMemo(() => {
    return (
      allianceCodesFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))
        ?.values || []
    );
  }, [allianceCodesFilter, isPerLegSearch, activeLeg]);

  const pricesFilterForActiveLeg = useMemo(() => {
    return (
      pricesFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))?.values || []
    );
  }, [pricesFilter, isPerLegSearch, activeLeg]);

  const providersFilterForActiveLeg = useMemo(() => {
    return (
      providersFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))?.values ||
      []
    );
  }, [providersFilter, isPerLegSearch, activeLeg]);

  const providerCodesFilterForActiveLeg = useMemo(
    () =>
      providerCodesFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))
        ?.values || [],
    [providerCodesFilter, isPerLegSearch, activeLeg],
  );

  const paymentMethodIdsFilterForActiveLeg = useMemo(
    () =>
      paymentMethodIdsFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))
        ?.values || [],
    [paymentMethodIdsFilter, isPerLegSearch, activeLeg],
  );

  const stopoverOptionsFilterForActiveLeg = useMemo(
    () =>
      stopoverOptionsFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))
        ?.values || [],
    [stopoverOptionsFilter, isPerLegSearch, activeLeg],
  );

  const stopoverAirportCodesFilterForActiveLeg = useMemo(
    () => stopoverAirportCodesFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [stopoverAirportCodesFilter, activeLeg],
  );

  const stopCodesFilterForActiveLeg = useMemo(
    () => stopCodesFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [stopCodesFilter, activeLeg],
  );
  const flightExperiencesFilterForActiveLeg = useMemo(
    () => flightExperiencesFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [flightExperiencesFilter, activeLeg],
  );
  const airlineCodesFilterForActiveLeg = useMemo(
    () => airlineCodesFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [airlineCodesFilter, activeLeg],
  );

  const stopoverDurationsFilterForActiveLeg = useMemo(
    () => stopoverDurationsFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [stopoverDurationsFilter, activeLeg],
  );
  const departDurationsFilterForActiveLeg = useMemo(
    () => departDurationsFilter.find((filter) => filter.leg === activeLeg)?.values || [],
    [departDurationsFilter, activeLeg],
  );

  const originAirportCodesFilterForActiveLeg = useMemo(
    () =>
      originAirportCodesFilter.find((filter) => filter.leg === (isPerLegSearch ? activeLeg : 0))
        ?.values || [],
    [originAirportCodesFilter, isPerLegSearch, activeLeg],
  );
  const destinationAirportCodesFilterForActiveLeg = useMemo(
    () =>
      destinationAirportCodesFilter.find(
        (filter) => filter.leg === (isPerLegSearch ? activeLeg : 0),
      )?.values || [],
    [destinationAirportCodesFilter, isPerLegSearch, activeLeg],
  );

  const airlineCodesFilterValues = useMemo(
    () => (airlineCodesFilter.length ? airlineCodesFilter : fallbackFilter),
    [airlineCodesFilter, fallbackFilter],
  );

  const flightExperiencesFilterValues = useMemo(
    () => (flightExperiencesFilter.length ? flightExperiencesFilter : fallbackFilter),
    [flightExperiencesFilter, fallbackFilter],
  );

  const stopCodesFilterValues = useMemo(
    () => (stopCodesFilter.length ? stopCodesFilter : fallbackFilter),
    [stopCodesFilter, fallbackFilter],
  );

  const stopoverAirportCodesFilterValues = useMemo(
    () => (stopoverAirportCodesFilter.length ? stopoverAirportCodesFilter : fallbackFilter),
    [stopoverAirportCodesFilter, fallbackFilter],
  );

  const stopoverDurationsFilterValues = useMemo(
    () => (stopoverDurationsFilter.length ? stopoverDurationsFilter : fallbackFilter),
    [stopoverDurationsFilter, fallbackFilter],
  );
  const departDurationsFilterValues = useMemo(
    () => (departDurationsFilter.length ? departDurationsFilter : fallbackFilter),
    [departDurationsFilter, fallbackFilter],
  );

  const currency = useSelector(getCurrencyState);
  const currentSite = useSelector(getCurrentSiteState);
  const locale = useSelector(getLocaleState);
  const user = useSelector(getUserState);
  const defaultExchangeRate = useSelector(getExchangeRateState);
  const priceOptions = useSelector(getPriceOptionsState);
  const { isApplyBaggageFilter } = useFlightContext();

  const [searchParams] = useSearchParams();

  const isOfferEngineEnabled = searchParams.get(IS_OFFER_ENGINE_ENABLED_PARAM) === 'true';
  const liveTestProviderCodesSearchParam = searchParams.get(
    FlightSearchResultSearchParams.LiveTestProviderCodes,
  );
  const tsCodeSearchParam = searchParams.get('ts_code');
  const wegoAdgroupSearchParam = searchParams.get('wg_adgroup');

  // This key is used to log the metasearch results in the backend to do partner specific debug
  const partnerKeyToEnableDebugMode = searchParams.get('partner_debug_key');
  const encodedExcludedProvidersParams = searchParams.get(EXCLUDED_PROVIDERS_PARAM);

  const bowOnly = searchParams.get('bow_only') == 'true';
  const wgInternalCampaign = getCookie('wg_internal_campaign');

  const decodedExcludedProviders = useMemo<Array<string>>(() => {
    if (encodedExcludedProvidersParams) {
      try {
        return atob(encodedExcludedProvidersParams).split(',');
      } catch (error) {
        return [];
      }
    }

    return [];
  }, [encodedExcludedProvidersParams]);

  const liveTestProviderCodes = useMemo(
    () => liveTestProviderCodesSearchParam?.split(',') ?? [],
    [liveTestProviderCodesSearchParam],
  );

  const shopcashClickId = useMemo<string>(() => {
    if (tsCodeSearchParam === 'c65cf' && !!wegoAdgroupSearchParam) {
      return wegoAdgroupSearchParam;
    }

    return '';
  }, [tsCodeSearchParam, wegoAdgroupSearchParam]);

  const {
    recordRenderTimeFromFirstDataAction,
    stopRecordingTimeToFirstFetchAction,
    startRecordingFetchTimeUntilFirstDataAction,
    stopRecordingFetchTimeUntilFirstDataAction,
    recordNumberOfFaresInFirstPoll,
    startRecordingFetchTimeUntilFirstSabreFareResult,
    stopRecordingFetchTimeUntilFirstSabreFareResult,
    recordFirstPollWithSabreFareAction,
    recordTimeForParsingFlightSearchResults,
    startRecordingTimeForEachPoll,
    stopRecordingTimeForEachPoll,
    startRecordingSearchApiLatency,
    stopRecordingSearchApiLatency,
    startRecordingSearchResponseProcessingTime,
    stopRecordingSearchResponseProcessingTime,
    startRecordingPollingLatency,
    stopRecordingPollingLatency,
    startRecordingPollingProcessingTime,
    stopRecordingPollingProcessingTime,
    startRecordingPollingProcessingTimeAndWaitingTime,
    stopRecordingPollingProcessingTimeAndWaitingTime,
    startRecordingAggregateResult,
    stopRecordingAggregateResult,
    startRecordingTripViewModelProcessing,
    stopRecordingTripViewModelProcessing,
    startRecordingFilterProcessing,
    stopRecordingFilterProcessing,
  } = useFlightSearchResultsDatadog();

  const checkForInvalidPartnerDebugKeyError = (err: string) => {
    if (/Error: 401$/.test(err)) {
      alert(err + ' - Invalid Partner Debug Key');
    }
  };

  const gtmTimingEventTracking = useCallback(
    (timingValue: number, timingVar: string) => {
      const gtmData = {
        event: 'eventTiming',
        timingCategory: 'flights',
        timingValue: timingValue,
        timingVar: timingVar,
      };
      gtmTrack(GTM_ID, gtmAuth, gtmPreview, gtmData);
    },
    [gtmAuth, gtmPreview],
  );

  useEffect(() => {
    let isParamsValid = true;

    if (legs.length === 0 || cabinClass === undefined || paymentMethods.length === 0) {
      isParamsValid = false;
    }

    if (isParamsValid) {
      stopRecordingTimeToFirstFetchAction();
      startRecordingFetchTimeUntilFirstDataAction();
      startRecordingFetchTimeUntilFirstSabreFareResult();

      const flightSearchObservable$ = flightSearchObservable(
        `${API_BASE_URL}/v3/metasearch/flights/searches`,
        currentSite.countryCode,
        locale,
        currency.code,
        legs,
        adultCount,
        childCount,
        infantCount,
        cabinClass!,
        paymentMethods,
        user,
        liveTestProviderCodes,
        shopcashClickId,
        wegoAnalyticsClientId,
        wegoAnalyticsClientSessionId,
        () => {
          setProgressPercent((prevValue) => (prevValue + 9 < 100 ? prevValue + 9 : prevValue));
        },
        recordNumberOfFaresInFirstPoll,
        startRecordingTimeForEachPoll,
        stopRecordingTimeForEachPoll,
        startRecordingSearchApiLatency,
        stopRecordingSearchApiLatency,
        startRecordingSearchResponseProcessingTime,
        stopRecordingSearchResponseProcessingTime,
        startRecordingPollingLatency,
        stopRecordingPollingLatency,
        startRecordingPollingProcessingTime,
        stopRecordingPollingProcessingTime,
        stopRecordingFetchTimeUntilFirstSabreFareResult,
        recordFirstPollWithSabreFareAction,
        startRecordingPollingProcessingTimeAndWaitingTime,
        stopRecordingPollingProcessingTimeAndWaitingTime,
        bowOnly,
        isOfferEngineEnabled,
        partnerKeyToEnableDebugMode,
        decodedExcludedProviders,
        (index) => setPollIndex(index),
        isCacheFlightSearch,
        () => {
          setShowTurnstileWidget(true);
        },
        {
          ...(!!wgInternalCampaign && { wgInternalCampaign }),
        },
      );

      const searchStartsAt = window.performance.now();

      const subscription = flightSearchObservable$.subscribe(
        flightSearchResultObserver(
          (result) => {
            if (!result) return;

            setAggregatedFlightSearchResult((prev) => {
              startRecordingAggregateResult();
              if (result.fares.length && !prev?.fares?.length) {
                stopRecordingFetchTimeUntilFirstDataAction({
                  flight_offer_engine_integration_ab_test_variant:
                    getOfferEngineIntegrationAbTestVariant(result.search),
                });

                // filters might make the list render nothing even though there are results from the API
                recordRenderTimeFromFirstDataAction({
                  onStop: () => setHasRenderedResults(true),
                });

                gtmTimingEventTracking(
                  (window.performance.now() - searchStartsAt) | 0,
                  'FirstResult_Duration',
                );
              }

              const parsingStartTime = window.performance.now();
              const aggregatedFlightSearchResult = aggregateFlightSearchResults(prev, result);

              stopRecordingAggregateResult();
              const parsingTime = window.performance.now() - parsingStartTime;
              recordTimeForParsingFlightSearchResults(parsingTime, result.fares.length);

              return aggregatedFlightSearchResult;
            });
          },
          () => {
            setIsLoading(false);
            setProgressPercent(100);
            gtmTimingEventTracking(
              (window.performance.now() - searchStartsAt) | 0,
              'FullResult_Duration',
            );
          },
          (err) => {
            setFlightMetaSearchOriginalError(err);
            checkForInvalidPartnerDebugKeyError(err);
            const errorCodeRegex = /Error: (\d+)/;
            const errorCodeMatch = err.toString().match(errorCodeRegex);

            setFlightMetaSearchError({
              status: errorCodeMatch ? errorCodeMatch[1] : '',
              message: err,
            });
          },
        ),
      );

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [
    adultCount,
    cabinClass,
    childCount,
    currency.code,
    currentSite.countryCode,
    infantCount,
    legs,
    liveTestProviderCodes,
    locale,
    paymentMethods,
    shopcashClickId,
    user,
    wegoAnalyticsClientId,
    bowOnly,
    wgInternalCampaign,
    isOfferEngineEnabled,
    stopRecordingTimeToFirstFetchAction,
    startRecordingFetchTimeUntilFirstDataAction,
    startRecordingFetchTimeUntilFirstSabreFareResult,
    stopRecordingFetchTimeUntilFirstSabreFareResult,
    wegoAnalyticsClientSessionId,
    recordNumberOfFaresInFirstPoll,
    partnerKeyToEnableDebugMode,
    stopRecordingFetchTimeUntilFirstDataAction,
    recordRenderTimeFromFirstDataAction,
    gtmTimingEventTracking,
    decodedExcludedProviders,
  ]);

  const airlinesHashMap = useMemo(
    () =>
      convertListToHashMap<FlightSearchResultAirline>(
        aggregatedFlightSearchResult.airlines,
        'code',
      ),
    [aggregatedFlightSearchResult.airlines],
  );

  const airportsHashMap = useMemo(
    () =>
      convertListToHashMap<FlightSearchResultAirport>(
        aggregatedFlightSearchResult.airports,
        'code',
      ),
    [aggregatedFlightSearchResult.airports],
  );

  const citiesHashMap = useMemo(
    () => convertListToHashMap<FlightSearchResultCity>(aggregatedFlightSearchResult.cities, 'code'),
    [aggregatedFlightSearchResult.cities],
  );

  const providersHashMap = useMemo(
    () =>
      convertListToHashMap<FlightSearchResultProvider>(
        aggregatedFlightSearchResult.providers,
        'code',
      ),
    [aggregatedFlightSearchResult.providers],
  );

  const airlineFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const airlineFilterOptions = aggregatedFlightSearchResult.filters.airlines
      .map((airlineFilter, index) => {
        const airline = airlinesHashMap[airlineFilter.code];

        if (!!airline) {
          return {
            id: index,
            name: airlineFilter.code,
            price: airlineFilter.price,
            airline: airline,
          } as FlightMetaSearchFilterOption;
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        return a.airline!.name.localeCompare(b.airline!.name);
      });

    stopRecordingFilterProcessing({
      name: 'airlineFilterOptions',
      index: pollIndex,
    });

    return airlineFilterOptions;
  }, [airlinesHashMap, aggregatedFlightSearchResult.filters.airlines]);

  const allianceFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const allianceFilterOptions = aggregatedFlightSearchResult.filters.alliances.map(
      (allianceFilter, index) => ({
        id: index,
        name: allianceFilter.code,
        price: allianceFilter.price,
      }),
    ) as FlightMetaSearchFilterOption[];

    stopRecordingFilterProcessing({
      name: 'allianceFilterOptions',
      index: pollIndex,
    });

    return allianceFilterOptions;
  }, [aggregatedFlightSearchResult.filters.alliances]);

  const destinationAirportFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const destinationAirportFilterOptions = aggregatedFlightSearchResult.filters.destinationAirports
      .map<FlightMetaSearchFilterOption | undefined>((destinationAirportFilter, index) => {
        const airport = airportsHashMap[destinationAirportFilter.code];
        const city = airport ? citiesHashMap[airport.cityCode] : undefined;

        if (city) {
          return {
            id: index,
            name: destinationAirportFilter.code,
            price: destinationAirportFilter.price,
            airport,
            city,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.city!.name.toLowerCase() < b.city!.name.toLowerCase()) {
          return -1;
        }

        if (a.city!.name.toLowerCase() > b.city!.name.toLowerCase()) {
          return 1;
        }

        return 0;
      })
      .filter(notUndefinedAndNull);

    stopRecordingFilterProcessing({
      name: 'destinationAirportFilterOptions',
      index: pollIndex,
    });

    return destinationAirportFilterOptions;
  }, [airportsHashMap, citiesHashMap, aggregatedFlightSearchResult.filters.destinationAirports]);

  const originAirportFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const originAirportFilterOptions = aggregatedFlightSearchResult.filters.originAirports
      .map((originAirportFilter, index) => {
        const airport = airportsHashMap[originAirportFilter.code];
        const city = airport ? citiesHashMap[airport.cityCode] : undefined;

        if (city) {
          return {
            id: index,
            name: originAirportFilter.code,
            price: originAirportFilter.price,
            airport,
            city,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.city!.name.toLowerCase() < b.city!.name.toLowerCase()) {
          return -1;
        }

        if (a.city!.name.toLowerCase() > b.city!.name.toLowerCase()) {
          return 1;
        }

        return 0;
      }) as FlightMetaSearchFilterOption[];

    stopRecordingFilterProcessing({
      name: 'originAirportFilterOptions',
      index: pollIndex,
    });

    return originAirportFilterOptions;
  }, [airportsHashMap, citiesHashMap, aggregatedFlightSearchResult.filters.originAirports]);

  const stopFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const stopFilterOptions = aggregatedFlightSearchResult.filters.stops
      .map((stopFilter) => {
        return {
          id: stopFilter.code === 'DIRECT' ? 0 : stopFilter.code === 'ONE_STOP' ? 1 : 2,
          name: stopFilter.code,
          price: stopFilter.price,
        };
      })
      .sort((a, b) => a.id - b.id) as FlightMetaSearchFilterOption[];

    stopRecordingFilterProcessing({
      name: 'stopFilterOptions',
      index: pollIndex,
    });

    return stopFilterOptions;
  }, [aggregatedFlightSearchResult.filters.stops]);

  const stopoverAirportFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const stopoverAirportFilterOptions = aggregatedFlightSearchResult.filters.stopoverAirports
      .map((stopoverAirportFilter, index) => {
        const airport = airportsHashMap[stopoverAirportFilter.code];
        const city = !!airport ? citiesHashMap[airport.cityCode] : undefined;

        if (!!city) {
          return {
            id: index,
            name: stopoverAirportFilter.code,
            price: stopoverAirportFilter.price,
            airport,
            city,
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => {
        if (a.city!.name.toLowerCase() < b.city!.name.toLowerCase()) {
          return -1;
        }

        if (a.city!.name.toLowerCase() > b.city!.name.toLowerCase()) {
          return 1;
        }

        return 0;
      }) as FlightMetaSearchFilterOption[];

    stopRecordingFilterProcessing({
      name: 'stopoverAirportFilterOptions',
      index: pollIndex,
    });

    return stopoverAirportFilterOptions;
  }, [airportsHashMap, citiesHashMap, aggregatedFlightSearchResult.filters.stopoverAirports]);

  const legFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const legFilterOptions = aggregatedFlightSearchResult.filters.legs
      .map((legFilter) => {
        const departureAirport = airportsHashMap[legFilter.departureAirportCode];
        const departureCity = citiesHashMap[legFilter.departureCityCode];

        const arrivalAirport = airportsHashMap[legFilter.arrivalAirportCode];
        const arrivalCity = citiesHashMap[legFilter.arrivalCityCode];

        if (departureAirport && departureCity && arrivalAirport && arrivalCity) {
          return {
            id: legFilter.index,
            name: `${legFilter.departureCityCode}-${legFilter.arrivalCityCode}-${legFilter.index}`,
            departureAirport,
            departureCity,
            arrivalAirport,
            arrivalCity,
            leg: {
              ...legFilter,
              airlines: legFilter.airlines.map((airline, index) => ({
                ...airline,
                id: index,
                name: airline.code,
                price: airline.price,
                airline: airlinesHashMap[airline.code],
              })),
              stopoverAirports: legFilter.stopoverAirports.map((airport) => ({
                ...airport,
                airport: airportsHashMap[airport.code],
                city: citiesHashMap[airport.code],
              })),
            },
          };
        }
      })
      .filter(notUndefinedAndNull)
      .sort((a, b) => a.id - b.id) as FlightMetaSearchFilterOption[];

    stopRecordingFilterProcessing({
      name: 'legFilterOptions',
      index: pollIndex,
    });

    return legFilterOptions;
  }, [aggregatedFlightSearchResult.filters.legs, airportsHashMap, citiesHashMap, airlinesHashMap]);

  const stopoverDurationFilterOptions = aggregatedFlightSearchResult.filters.stopoverDurations;

  const exchangeRates: ExchangeRate[] = useMemo(
    () =>
      aggregatedFlightSearchResult.currencies?.map((cur) => ({
        base: '',
        code: cur.code,
        rate: cur.rate,
      })) || [],
    [aggregatedFlightSearchResult.currencies],
  );

  const exchangeRate =
    exchangeRates.find((exchangeRate) => exchangeRate.code === currency.code) ??
    defaultExchangeRate;

  const search = aggregatedFlightSearchResult?.search;

  const tripViewModels: TripViewModel[] = useMemo(() => {
    startRecordingTripViewModelProcessing();
    const trips = aggregatedFlightSearchResult.trips;
    const fares = aggregatedFlightSearchResult.fares;
    const legs = aggregatedFlightSearchResult.legs;

    const faresGroupByTripId = groupBy(fares, 'tripId');

    const tripViewModels$ = trips.map((trip) => {
      const filteredByTripIdFares = faresGroupByTripId[trip.id] || [];
      const sortedFilteredByTripIdFares = sortFlightFares(filteredByTripIdFares, 'asc', true);

      const fareViewModels = sortedFilteredByTripIdFares.map((fare) => {
        const provider = providersHashMap[fare.providerCode];

        return { fare, provider };
      });

      const legViewModels = trip.legIds.map((legId) => {
        const leg = legs.find((leg) => leg.id === legId)!;
        const segmentsWithAirportNames = leg.segments.map((segment) => {
          segment.arrivalAirport = airportsHashMap[segment.arrivalAirportCode];
          segment.departureAirport = airportsHashMap[segment.departureAirportCode];
          segment.airline = airlinesHashMap[segment.airlineCode];
          return segment;
        });
        leg.segments = segmentsWithAirportNames;
        const distinctLegAirlineCodes = [...new Set(leg.airlineCodes)];

        const legAirlines = distinctLegAirlineCodes.map<FlightSearchResultAirline>(
          (distinctLegAirlineCode) => airlinesHashMap[distinctLegAirlineCode],
        );

        const legTotalDurationMinutes = leg.durationMinutes;
        const legSegmentViewModels: SegmentViewModel[] = [];

        let accumulateDurationMinutes = 0;

        for (let i = 0; i < leg.segments.length - 1; i++) {
          const segment = leg.segments[i];
          segment.arrivalAirport = airportsHashMap[segment.arrivalAirportCode];
          segment.departureAirport = airportsHashMap[segment.departureAirportCode];

          legSegmentViewModels.push({
            segment: segment,
            pointStartPercentage:
              ((accumulateDurationMinutes + segment.durationMinutes) / legTotalDurationMinutes) *
              100,
            pointWidthPercentage:
              (segment.stopoverDurationMinutes! / legTotalDurationMinutes) * 100,
          });

          accumulateDurationMinutes += segment.durationMinutes + segment.stopoverDurationMinutes!;
        }

        return {
          airlines: legAirlines,
          arrivalAirport: airportsHashMap[leg.arrivalAirportCode],
          departureAirport: airportsHashMap[leg.departureAirportCode],
          leg,
          segmentViewModels: legSegmentViewModels,
        };
      });

      return {
        fares: fareViewModels,
        filteredFares: fareViewModels,
        legs: legViewModels,
        tripId: trip.id,
        normalizedFlight: trip.normalizedFlight,
        supplement: trip.supplement,
      };
    });

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'tripViewModels',
      numberOfFares: fares.length,
      numberOfLegs: legs.length,
      numberOfTrips: trips.length,
    });

    return tripViewModels$;
  }, [
    aggregatedFlightSearchResult.fares,
    aggregatedFlightSearchResult.legs,
    aggregatedFlightSearchResult.trips,
    airlinesHashMap,
    airportsHashMap,
    providersHashMap,
  ]);

  const sponsoredAdTripViewModels = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const trips = aggregatedFlightSearchResult.trips;
    const legs = aggregatedFlightSearchResult.legs;

    const tripMapById = convertListToHashMap<FlightSearchResultTrip>(trips, 'id');

    const tripViewModels = aggregatedFlightSearchResult.sponsors
      .map((sponsoredFare, index) => {
        const trip = tripMapById[sponsoredFare.fareView?.tripId || ''];

        if (trip) {
          const legViewModels = trip.legIds.map((legId) => {
            const leg = legs.find((leg) => leg.id === legId)!;
            const segmentsWithAirportNames = leg.segments.map((segment) => {
              segment.arrivalAirport = airportsHashMap[segment.arrivalAirportCode];
              segment.departureAirport = airportsHashMap[segment.departureAirportCode];
              return segment;
            });
            leg.segments = segmentsWithAirportNames;
            const distinctLegAirlineCodes = [...new Set(leg.airlineCodes)];

            const legAirlines = distinctLegAirlineCodes.map<FlightSearchResultAirline>(
              (distinctLegAirlineCode) => airlinesHashMap[distinctLegAirlineCode],
            );

            const legTotalDurationMinutes = leg.durationMinutes;
            const legSegmentViewModels: Array<SegmentViewModel> = [];

            let accumulateDurationMinutes = 0;

            for (let i = 0; i < leg.segments.length - 1; i++) {
              const segment = leg.segments[i];
              segment.arrivalAirport = airportsHashMap[segment.arrivalAirportCode];
              segment.departureAirport = airportsHashMap[segment.departureAirportCode];

              legSegmentViewModels.push({
                segment: segment,
                pointStartPercentage:
                  ((accumulateDurationMinutes + segment.durationMinutes) /
                    legTotalDurationMinutes) *
                  100,
                pointWidthPercentage:
                  (segment.stopoverDurationMinutes! / legTotalDurationMinutes) * 100,
              });

              accumulateDurationMinutes +=
                segment.durationMinutes + segment.stopoverDurationMinutes!;
            }

            return {
              airlines: legAirlines,
              arrivalAirport: airportsHashMap[leg.arrivalAirportCode],
              departureAirport: airportsHashMap[leg.departureAirportCode],
              leg,
              segmentViewModels: legSegmentViewModels,
            };
          });

          const provider = providersHashMap[sponsoredFare.fareView!.providerCode];

          const fareViewModel: FareViewModel = { fare: sponsoredFare.fareView!, provider };

          const tripViewModel: TripViewModel = {
            fares: [fareViewModel],
            filteredFares: [fareViewModel],
            legs: legViewModels,
            tripId: trip.id,
            sponsored: {
              brandingColor: sponsoredFare.brandingColor,
              brandingDescription: sponsoredFare.brandingDescription,
              brandingHeader: sponsoredFare.brandingHeader,
              partnerLogoUrl: sponsoredFare.partnerLogoUrl,
              sponsoredAdCode: sponsoredFare.sponsoredAdCode,
              adPosition: index + 1,
            },
            normalizedFlight: trip.normalizedFlight,
            supplement: trip.supplement,
          };

          return tripViewModel;
        }

        return undefined;
      })
      .filter(notUndefinedAndNull);

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'sponsoredAdTripViewModels',
      numberOfLegs: legs.length,
      numberOfTrips: trips.length,
    });

    return tripViewModels;
  }, [
    aggregatedFlightSearchResult.legs,
    aggregatedFlightSearchResult.sponsors,
    aggregatedFlightSearchResult.trips,
    airlinesHashMap,
    airportsHashMap,
    providersHashMap,
  ]);

  const providerFilterOptions = useMemo(
    () => aggregatedFlightSearchResult.providers.sort((a, b) => a.name.localeCompare(b.name)),
    [aggregatedFlightSearchResult.providers],
  );

  const filteredTripViewModelsBasedOnFilterOptions = useMemo<Array<TripViewModel>>(() => {
    startRecordingTripViewModelProcessing();

    if (isPerLegSearch) {
      const filteredTripViewModelsBasedOnFilterOptionsPLS = filterTripViewModelsBasedOnOneLeg(
        activeLeg,
        tripViewModels,
        stopCodesFilterForActiveLeg,
        flightExperiencesFilterForActiveLeg,
        pricesFilterForActiveLeg,
        airlineCodesFilterForActiveLeg,
        allianceCodesFilterForActiveLeg,
        providerCodesFilterForActiveLeg,
        providersFilterForActiveLeg,
        paymentMethodIdsFilterForActiveLeg,
        departLegDepartureTimeBlocksFilter,
        departLegArrivalTimeBlocksFilter,
        returnLegDepartureTimeBlocksFilter,
        returnLegArrivalTimeBlocksFilter,
        stopoverAirportCodesFilterForActiveLeg,
        stopoverOptionsFilterForActiveLeg,
        stopoverDurationsFilterForActiveLeg,
        departDurationsFilterForActiveLeg,
        originAirportCodesFilterForActiveLeg,
        destinationAirportCodesFilterForActiveLeg,
        priceOptions,
        isApplyBaggageFilter,
      );

      // pollIndex is used only for tracking. Dont add to dependencies
      stopRecordingTripViewModelProcessing({
        index: pollIndex,
        name: 'filteredTripViewModelsBasedOnFilterOptions',
        numberOfTripViewModels: filteredTripViewModelsBasedOnFilterOptionsPLS.length,
      });

      return filteredTripViewModelsBasedOnFilterOptionsPLS;
    }

    const filteredTripViewModelsBasedOnFilterOptionsNonPLS = isMultiCity
      ? filterMultiCityTripViewModelBasedOnFilters({
          tripViewModels,
          selectedStopCodes: stopCodesFilterValues,
          selectedFlightExperiences: flightExperiencesFilterValues,
          selectedAirlineCodes: airlineCodesFilterValues,
          selectedStopoverAirportCodes: stopoverAirportCodesFilterValues,
          selectedStopoverDuration: stopoverDurationsFilterValues,
          selectedDepartDuration: departDurationsFilterValues,
          selectedDepartLegDepartureTimeBlocks: departLegDepartureTimeBlocksFilter,
          selectedDepartLegArrivalTimeBlocks: departLegArrivalTimeBlocksFilter,
          selectedUsdPrices: pricesFilterForActiveLeg,
          priceOptions,
          selectedLegs,
        })
      : filterOnewayAndRoundTripViewModelBasedOnFilters({
          tripViewModels,
          selectedPaymentMethodIds: paymentMethodIdsFilterForActiveLeg,
          selectedStopCodes: stopCodesFilterValues,
          selectedProviders: providersFilterForActiveLeg,
          selectedFareProviderCodes: providerCodesFilterForActiveLeg,
          selectedFlightExperiences: flightExperiencesFilterValues,
          selectedAirlineCodes: airlineCodesFilterValues,
          selectedAllianceCodes: allianceCodesFilterForActiveLeg,
          selectedTripOptions: tripsFilter,
          selectedOriginAirportCodes: originAirportCodesFilterForActiveLeg,
          selectedDestinationAirportCodes: destinationAirportCodesFilterForActiveLeg,
          selectedStopoverAirportCodes: stopoverAirportCodesFilterValues,
          selectedStopoverOptions: stopoverOptionsFilterForActiveLeg,
          selectedStopoverDuration: stopoverDurationsFilterValues,
          selectedDepartDuration: departDurationsFilterValues,
          selectedReturnDuration: returnDurationsFilter,
          selectedDepartLegDepartureTimeBlocks: departLegDepartureTimeBlocksFilter,
          selectedDepartLegArrivalTimeBlocks: departLegArrivalTimeBlocksFilter,
          selectedReturnLegDepartureTimeBlocks: returnLegDepartureTimeBlocksFilter,
          selectedReturnLegArrivalTimeBlocks: returnLegArrivalTimeBlocksFilter,
          selectedUsdPrices: pricesFilterForActiveLeg,
          priceOptions,
          selectedLegs,
          isApplyBaggageFilter,
        });

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'filteredTripViewModelsBasedOnFilterOptions',
      numberOfTripViewModels: filteredTripViewModelsBasedOnFilterOptionsNonPLS.length,
    });

    return filteredTripViewModelsBasedOnFilterOptionsNonPLS;
  }, [
    activeLeg,
    airlineCodesFilterForActiveLeg,
    airlineCodesFilterValues,
    allianceCodesFilterForActiveLeg,
    departDurationsFilterForActiveLeg,
    departDurationsFilterValues,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    destinationAirportCodesFilterForActiveLeg,
    flightExperiencesFilterForActiveLeg,
    flightExperiencesFilterValues,
    isApplyBaggageFilter,
    isMultiCity,
    isPerLegSearch,
    originAirportCodesFilterForActiveLeg,
    paymentMethodIdsFilterForActiveLeg,
    priceOptions,
    pricesFilterForActiveLeg,
    providerCodesFilterForActiveLeg,
    providersFilterForActiveLeg,
    returnDurationsFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    selectedLegs,
    stopCodesFilterForActiveLeg,
    stopCodesFilterValues,
    stopoverAirportCodesFilterForActiveLeg,
    stopoverAirportCodesFilterValues,
    stopoverDurationsFilterForActiveLeg,
    stopoverDurationsFilterValues,
    stopoverOptionsFilterForActiveLeg,
    tripViewModels,
    tripsFilter,
  ]);

  const filteredTripViewModelsGroupedByDepartLeg = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const filteredTripViewModelsGroupedByDepartLeg$ = groupTripViewModelsBasedOnDepartLeg(
      filteredTripViewModelsBasedOnFilterOptions,
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'filteredTripViewModelsGroupedByDepartLeg',
      numberOfTripViewModels: filteredTripViewModelsGroupedByDepartLeg$.length,
    });

    return filteredTripViewModelsGroupedByDepartLeg$;
  }, [filteredTripViewModelsBasedOnFilterOptions]);

  const filteredSponsoredAdTripViewModels = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const filteredSponsoredAdTripViewModels$ = isMultiCity
      ? filterMultiCityTripViewModelBasedOnFilters({
          tripViewModels: sponsoredAdTripViewModels,
          selectedStopCodes: stopCodesFilterValues,
          selectedFlightExperiences: flightExperiencesFilterValues,
          selectedAirlineCodes: airlineCodesFilterValues,
          selectedStopoverAirportCodes: stopoverAirportCodesFilterValues,
          selectedStopoverDuration: stopoverDurationsFilterValues,
          selectedDepartDuration: departDurationsFilterValues,
          selectedDepartLegDepartureTimeBlocks: departLegDepartureTimeBlocksFilter,
          selectedDepartLegArrivalTimeBlocks: departLegArrivalTimeBlocksFilter,
          selectedUsdPrices: pricesFilterForActiveLeg,
          priceOptions,
          selectedLegs,
        })
      : filterOnewayAndRoundTripViewModelBasedOnFilters({
          tripViewModels: sponsoredAdTripViewModels,
          selectedPaymentMethodIds: paymentMethodIdsFilterForActiveLeg,
          selectedStopCodes: stopCodesFilterValues,
          selectedProviders: providersFilterForActiveLeg,
          selectedFareProviderCodes: providerCodesFilterForActiveLeg,
          selectedFlightExperiences: flightExperiencesFilterValues,
          selectedAirlineCodes: airlineCodesFilterValues,
          selectedAllianceCodes: allianceCodesFilterForActiveLeg,
          selectedTripOptions: tripsFilter,
          selectedOriginAirportCodes: originAirportCodesFilterForActiveLeg,
          selectedDestinationAirportCodes: destinationAirportCodesFilterForActiveLeg,
          selectedStopoverAirportCodes: stopoverAirportCodesFilterValues,
          selectedStopoverOptions: stopoverOptionsFilterForActiveLeg,
          selectedStopoverDuration: stopoverDurationsFilterValues,
          selectedDepartDuration: departDurationsFilterValues,
          selectedReturnDuration: returnDurationsFilter,
          selectedDepartLegDepartureTimeBlocks: departLegDepartureTimeBlocksFilter,
          selectedDepartLegArrivalTimeBlocks: departLegArrivalTimeBlocksFilter,
          selectedReturnLegDepartureTimeBlocks: returnLegDepartureTimeBlocksFilter,
          selectedReturnLegArrivalTimeBlocks: returnLegArrivalTimeBlocksFilter,
          selectedUsdPrices: pricesFilterForActiveLeg,
          priceOptions,
          selectedLegs,
          isApplyBaggageFilter,
        });

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'filteredSponsoredAdTripViewModels',
      numberOfTripViewModels: filteredSponsoredAdTripViewModels$.length,
    });

    return filteredSponsoredAdTripViewModels$;
  }, [
    airlineCodesFilterValues,
    allianceCodesFilterForActiveLeg,
    departDurationsFilterValues,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    destinationAirportCodesFilterForActiveLeg,
    flightExperiencesFilterValues,
    isApplyBaggageFilter,
    isMultiCity,
    originAirportCodesFilterForActiveLeg,
    paymentMethodIdsFilterForActiveLeg,
    priceOptions,
    pricesFilterForActiveLeg,
    providerCodesFilterForActiveLeg,
    providersFilterForActiveLeg,
    returnDurationsFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    selectedLegs,
    sponsoredAdTripViewModels,
    stopCodesFilterValues,
    stopoverAirportCodesFilterValues,
    stopoverDurationsFilterValues,
    stopoverOptionsFilterForActiveLeg,
    tripsFilter,
  ]);

  const sortedAndFilteredTripViewModelsForPerLegSearch = useMemo(() => {
    startRecordingTripViewModelProcessing();

    if (isPLSDepartLegSelection) {
      const departLegGroupsFilteredBasedOnReturnLeg = filteredTripViewModelsGroupedByDepartLeg.map(
        (departLegGroup: DepartLegGroup) => {
          const tripViewModelsWithReturnLegSatisfyingFilterConditions =
            filterTripViewModelsBasedOnOneLeg(
              1,
              departLegGroup.tripViewModels,
              stopCodesFilterForActiveLeg,
              flightExperiencesFilterForActiveLeg,
              pricesFilterForActiveLeg,
              airlineCodesFilterForActiveLeg,
              allianceCodesFilterForActiveLeg,
              providerCodesFilterForActiveLeg,
              providersFilterForActiveLeg,
              paymentMethodIdsFilterForActiveLeg,
              departLegDepartureTimeBlocksFilter,
              departLegArrivalTimeBlocksFilter,
              returnLegDepartureTimeBlocksFilter,
              returnLegArrivalTimeBlocksFilter,
              stopoverAirportCodesFilterForActiveLeg,
              stopoverOptionsFilterForActiveLeg,
              stopoverDurationsFilterForActiveLeg,
              [],
              originAirportCodesFilterForActiveLeg,
              destinationAirportCodesFilterForActiveLeg,
              priceOptions,
              isApplyBaggageFilter,
            );

          if (tripViewModelsWithReturnLegSatisfyingFilterConditions.length) {
            departLegGroup.tripViewModels = tripViewModelsWithReturnLegSatisfyingFilterConditions;
          }

          return departLegGroup;
        },
      );

      const sortedAndFilteredTripViewModelsForPerLegSearchDepart = sortDepartLegGroups(
        departLegGroupsFilteredBasedOnReturnLeg,
        sortType,
        sortOrder,
      ).map((departLegGroup) => departLegGroup.tripViewModels[0]);

      // pollIndex is used only for tracking. Dont add to dependencies
      stopRecordingTripViewModelProcessing({
        index: pollIndex,
        name: 'sortedAndFilteredTripViewModelsForPerLegSearch',
        numberOfTripViewModels: sortedAndFilteredTripViewModelsForPerLegSearchDepart.length,
      });

      return sortedAndFilteredTripViewModelsForPerLegSearchDepart;
    } else {
      const tripViewModelsForSelectedDepartLeg =
        filteredTripViewModelsGroupedByDepartLeg.find(
          (group) => group.departLegId === currentlySelectedLegIdPLS,
        )?.tripViewModels || [];

      const sortedAndFilteredTripViewModelsForPerLegSearchReturn = sortTripViewModels(
        tripViewModelsForSelectedDepartLeg,
        sortType,
        sortOrder,
      );

      // pollIndex is used only for tracking. Dont add to dependencies
      stopRecordingTripViewModelProcessing({
        index: pollIndex,
        name: 'sortedAndFilteredTripViewModelsForPerLegSearch',
        numberOfTripViewModels: sortedAndFilteredTripViewModelsForPerLegSearchReturn.length,
      });

      return sortedAndFilteredTripViewModelsForPerLegSearchReturn;
    }
  }, [
    airlineCodesFilterForActiveLeg,
    allianceCodesFilterForActiveLeg,
    currentlySelectedLegIdPLS,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    destinationAirportCodesFilterForActiveLeg,
    filteredTripViewModelsGroupedByDepartLeg,
    flightExperiencesFilterForActiveLeg,
    isApplyBaggageFilter,
    isPLSDepartLegSelection,
    originAirportCodesFilterForActiveLeg,
    paymentMethodIdsFilterForActiveLeg,
    priceOptions,
    pricesFilterForActiveLeg,
    providerCodesFilterForActiveLeg,
    providersFilterForActiveLeg,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    sortOrder,
    sortType,
    stopCodesFilterForActiveLeg,
    stopoverAirportCodesFilterForActiveLeg,
    stopoverDurationsFilterForActiveLeg,
    stopoverOptionsFilterForActiveLeg,
  ]);

  const tripViewModelsSortedBasedOnPrice = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const tripViewModelsSortedBasedOnPrice$ = sortTripViewModels(
      filteredTripViewModelsBasedOnFilterOptions,
      'price',
      'asc',
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'tripViewModelsSortedBasedOnPrice',
      numberOfTripViewModels: tripViewModelsSortedBasedOnPrice$.length,
    });

    return tripViewModelsSortedBasedOnPrice$;
  }, [filteredTripViewModelsBasedOnFilterOptions]);

  const tripViewModelsSortedBasedOnDuration = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const tripViewModelsSortedBasedOnDuration$ = sortTripViewModels(
      filteredTripViewModelsBasedOnFilterOptions,
      'duration',
      'asc',
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'tripViewModelsSortedBasedOnDuration',
      numberOfTripViewModels: tripViewModelsSortedBasedOnDuration$.length,
    });

    return tripViewModelsSortedBasedOnDuration$;
  }, [filteredTripViewModelsBasedOnFilterOptions]);

  const tripViewModelsSortedBasedOnScore = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const tripViewModelsSortedBasedOnScore$ = sortTripViewModels(
      filteredTripViewModelsBasedOnFilterOptions,
      'score',
      'desc',
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'tripViewModelsSortedBasedOnScore',
      numberOfTripViewModels: tripViewModelsSortedBasedOnScore$.length,
    });

    return tripViewModelsSortedBasedOnScore$;
  }, [filteredTripViewModelsBasedOnFilterOptions]);

  const sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds = useMemo<
    Array<TripViewModel>
  >(() => {
    if (sortType === 'price' && sortOrder === 'asc') {
      return tripViewModelsSortedBasedOnPrice;
    }

    if (sortType === 'duration' && sortOrder === 'asc') {
      return tripViewModelsSortedBasedOnDuration;
    }
    if (sortType === 'score' && sortOrder === 'desc') {
      return tripViewModelsSortedBasedOnScore;
    }
    startRecordingTripViewModelProcessing();

    const sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds$ = sortTripViewModels(
      filteredTripViewModelsBasedOnFilterOptions,
      sortType,
      sortOrder,
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds',
      numberOfTripViewModels:
        sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds$.length,
    });

    return sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds$;
  }, [
    filteredTripViewModelsBasedOnFilterOptions,
    sortOrder,
    sortType,
    tripViewModelsSortedBasedOnDuration,
    tripViewModelsSortedBasedOnPrice,
    tripViewModelsSortedBasedOnScore,
  ]);

  const sortedAndFilteredTripViewModels = useMemo<Array<TripViewModel>>(() => {
    startRecordingTripViewModelProcessing();

    const sortedAndFilteredTripViewModelsWithSponsoredAdsAndLeaderboardAds = [
      ...sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds,
    ];

    leaderboardAdsAndInlineAdsConfig.forEach((ad) => {
      const realAdPosition = getRealAdPosition(ad, leaderboardAdsAndInlineAdsConfig);
      if (ad.type === 'leaderboard' || ad.type === 'native') {
        if (
          sortedAndFilteredTripViewModelsWithSponsoredAdsAndLeaderboardAds.length >= realAdPosition
        ) {
          sortedAndFilteredTripViewModelsWithSponsoredAdsAndLeaderboardAds.splice(
            realAdPosition,
            0,
            {
              fares: [],
              filteredFares: [],
              legs: [],
              tripId: '',
              isPlaceholder: true,
              isNativeAd: ad.type === 'native',
              isAdVisible: ad.visible,
              id: ad.id,
            },
          );
        }
      } else if (ad.type === 'inline') {
        if (ad.visible && ad.adItem) {
          sortedAndFilteredTripViewModelsWithSponsoredAdsAndLeaderboardAds.splice(
            realAdPosition,
            0,
            ad.adItem,
          );
        }
      }
    });

    // Sort type price use grouping so we need to handle ISA and leaderboard ads after grouping
    // Hide ISA for flight search 2 non multi city
    const sortedAndFilteredTripViewModels$ =
      selectedLegs.length > 0 || sortType === 'price' || isFlightSearch2AndNotMulticity
        ? sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds
        : sortedAndFilteredTripViewModelsWithSponsoredAdsAndLeaderboardAds.filter(
            (item) => item.filteredFares.length || item.isPlaceholder,
          );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'sortedAndFilteredTripViewModels',
      numberOfTripViewModels: sortedAndFilteredTripViewModels$.length,
    });

    return sortedAndFilteredTripViewModels$;
  }, [
    leaderboardAdsAndInlineAdsConfig,
    selectedLegs.length,
    sortType,
    sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds,
    isFlightSearch2AndNotMulticity,
  ]);

  const sortedAndFilteredTripViewModelsForRecommendedSortFlight2 = useMemo<
    Array<TripViewModel>
  >(() => {
    if (isPerLegSearch) {
      return sortedAndFilteredTripViewModelsForPerLegSearch;
    }

    return sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds;
  }, [
    isPerLegSearch,
    sortedAndFilteredTripViewModelsForPerLegSearch,
    sortedAndFilteredTripViewModelsWithoutSponsoredAdsAndLeaderboardAds,
  ]);

  const sortedAndFilteredTripViewModelsWithoutPlaceholder = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const sortedAndFilteredTripViewModelsWithoutPlaceholder$ =
      sortedAndFilteredTripViewModels.filter(
        (tripViewModel) => !tripViewModel.isPlaceholder && !tripViewModel.headerType,
      );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'sortedAndFilteredTripViewModelsWithoutPlaceholder',
      numberOfTripViewModels: sortedAndFilteredTripViewModelsWithoutPlaceholder$.length,
    });

    return sortedAndFilteredTripViewModelsWithoutPlaceholder$;
  }, [sortedAndFilteredTripViewModels]);

  // Grouping the trip view models based on Airline code and price for price sort
  const groupedAndSortedAndFilteredTripViewModelMap = useMemo<{
    [key: string]: Array<TripViewModel>;
  }>(() => {
    startRecordingTripViewModelProcessing();

    const groupedAndSortedAndFilteredTripViewModelMap$ =
      groupTripViewModelBasedOnOutboundAirlineCodeAndPriceMap(sortedAndFilteredTripViewModels);

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'groupedAndSortedAndFilteredTripViewModelMap',
    });

    return groupedAndSortedAndFilteredTripViewModelMap$;
  }, [sortedAndFilteredTripViewModels]);

  const originalGroupedTripViewModelsDetail = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const originalGroupedTripViewModelsDetail$ = Object.keys(
      groupedAndSortedAndFilteredTripViewModelMap,
    ).reduce(
      (
        accumulator: Array<{
          key: string;
          index: number;
          maxLength: number;
          tripViewModel: TripViewModel;
        }>,
        key: string,
      ) => {
        const groupedTripViewModel = groupedAndSortedAndFilteredTripViewModelMap[key];

        if (expandedGroupKeys.includes(key)) {
          const tripViewModels = groupedTripViewModel.map((tripViewModel, index) => {
            return { key, index, tripViewModel, maxLength: groupedTripViewModel.length };
          });
          return [...accumulator, ...tripViewModels];
        }
        return [
          ...accumulator,
          {
            key: key,
            index: 0,
            tripViewModel: groupedAndSortedAndFilteredTripViewModelMap[key][0],
            maxLength: groupedTripViewModel.length,
          },
        ];
      },
      [],
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'originalGroupedTripViewModelsDetail',
    });

    return originalGroupedTripViewModelsDetail$;
  }, [expandedGroupKeys, groupedAndSortedAndFilteredTripViewModelMap]);

  const groupedTripViewModelsDetail = useMemo<
    Array<{ key: string; index: number; maxLength: number; tripViewModel: TripViewModel }>
  >(() => {
    startRecordingTripViewModelProcessing();

    const groupedResultWithPlaceHoldersAndLeaderboardAds = [...originalGroupedTripViewModelsDetail];

    leaderboardAdsAndInlineAdsConfig.forEach((ad) => {
      const realAdPosition = getRealAdPosition(ad, leaderboardAdsAndInlineAdsConfig);
      if (ad.type === 'leaderboard' || ad.type === 'native') {
        if (groupedResultWithPlaceHoldersAndLeaderboardAds.length >= realAdPosition) {
          groupedResultWithPlaceHoldersAndLeaderboardAds.splice(realAdPosition, 0, {
            index: realAdPosition,
            key: `placeholder-${realAdPosition}`,
            maxLength: 1,
            tripViewModel: {
              fares: [],
              filteredFares: [],
              legs: [],
              tripId: '',
              isPlaceholder: true,
              isAdVisible: ad.visible,
              isNativeAd: ad.type === 'native',
              id: ad.id,
            },
          });
        }
      } else if (ad.type === 'inline') {
        if (ad.visible && ad.adItem) {
          groupedResultWithPlaceHoldersAndLeaderboardAds.splice(realAdPosition, 0, {
            index: realAdPosition,
            key: `sponsored-${realAdPosition}`,
            maxLength: 1,
            tripViewModel: ad.adItem,
          });
        }
      }
    });

    // Hide ISA for flight search 2 non multi city
    const groupedTripViewModelsDetail$ =
      selectedLegs.length > 0 || isFlightSearch2AndNotMulticity
        ? originalGroupedTripViewModelsDetail
        : groupedResultWithPlaceHoldersAndLeaderboardAds;

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'groupedTripViewModelsDetail',
    });

    return groupedTripViewModelsDetail$;
  }, [
    originalGroupedTripViewModelsDetail,
    leaderboardAdsAndInlineAdsConfig,
    selectedLegs.length,
    isFlightSearch2AndNotMulticity,
  ]);

  const finalTripViewModelsToShowBasedOnSortType = useMemo(() => {
    if (isPerLegSearch) {
      return sortedAndFilteredTripViewModelsForPerLegSearch;
    }

    if (sortType === 'price') {
      return groupedTripViewModelsDetail;
    }

    return sortedAndFilteredTripViewModels;
  }, [
    groupedTripViewModelsDetail,
    isPerLegSearch,
    sortType,
    sortedAndFilteredTripViewModels,
    sortedAndFilteredTripViewModelsForPerLegSearch,
  ]);

  const isPollFailingMidway = useMemo(() => {
    return tripViewModels.length > 0 && !!flightMetaSearchOriginalError;
  }, [flightMetaSearchOriginalError, tripViewModels.length]);

  const groupedTripViewModelDetailWithoutPlaceholder = useMemo(() => {
    startRecordingTripViewModelProcessing();

    const groupedTripViewModelDetailWithoutPlaceholder$ = groupedTripViewModelsDetail.filter(
      (group) => !group.tripViewModel.isPlaceholder,
    );

    // pollIndex is used only for tracking. Dont add to dependencies
    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'groupedTripViewModelDetailWithoutPlaceholder',
    });

    return groupedTripViewModelDetailWithoutPlaceholder$;
  }, [groupedTripViewModelsDetail]);

  const cheapestFareViewModel = useMemo(() => {
    startRecordingTripViewModelProcessing();
    if (!filteredTripViewModelsBasedOnFilterOptions.length) return;

    const groupedTripViewModelMap = groupTripViewModelBasedOnOutboundAirlineCodeAndPriceMap(
      tripViewModelsSortedBasedOnPrice,
    );
    const groupedTirpViewModel = Object.keys(groupedTripViewModelMap).reduce(
      (
        accumulator: Array<{
          key: string;
          index: number;
          maxLength: number;
          tripViewModel: TripViewModel;
        }>,
        key: string,
      ) => {
        const groupedTripViewModel = groupedTripViewModelMap[key];
        return [
          ...accumulator,
          {
            key: key,
            index: 0,
            tripViewModel: groupedTripViewModelMap[key][0],
            maxLength: groupedTripViewModel.length,
          },
        ];
      },
      [],
    );

    const cheapestTrip = groupedTirpViewModel[0].tripViewModel;

    const cheapestFareViewModel = cheapestTrip.filteredFares[0];

    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'cheapestFareViewModel',
    });

    return cheapestFareViewModel;
  }, [filteredTripViewModelsBasedOnFilterOptions.length, tripViewModelsSortedBasedOnPrice]);

  const cheapestFare = useMemo(() => {
    startRecordingTripViewModelProcessing();
    if (!cheapestFareViewModel) return;

    const cheapestFare = convertFlightSearchResultFarePriceToPrice(
      cheapestFareViewModel.fare.price,
      passengerCount,
      priceOptions.showTotalPrice,
      true,
    );

    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'cheapestFare',
    });

    return cheapestFare;
  }, [cheapestFareViewModel, passengerCount, priceOptions.showTotalPrice]);

  const fastestCheapestFare = useMemo(() => {
    startRecordingTripViewModelProcessing();
    if (!filteredTripViewModelsBasedOnFilterOptions.length) return;

    const fastestTrip = tripViewModelsSortedBasedOnDuration[0];

    const fastestFareViewModel = fastestTrip.filteredFares[0];

    if (fastestFareViewModel) {
      const fastestFare = convertFlightSearchResultFarePriceToPrice(
        fastestFareViewModel.fare.price,
        passengerCount,
        priceOptions.showTotalPrice,
        true,
      );

      stopRecordingTripViewModelProcessing({
        index: pollIndex,
        name: 'fastestCheapestFare',
      });

      return fastestFare;
    }
  }, [
    filteredTripViewModelsBasedOnFilterOptions.length,
    passengerCount,
    priceOptions.showTotalPrice,
    tripViewModelsSortedBasedOnDuration,
  ]);

  const bestExperienceCheapestFareViewModel = useMemo(() => {
    startRecordingTripViewModelProcessing();
    if (!filteredTripViewModelsBasedOnFilterOptions.length) return;

    const bestExperienceTrip = tripViewModelsSortedBasedOnScore[0];

    const bestExperienceFareViewModel = bestExperienceTrip.filteredFares[0];

    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'bestExperienceCheapestFareViewModel',
    });

    return bestExperienceFareViewModel;
  }, [filteredTripViewModelsBasedOnFilterOptions.length, tripViewModelsSortedBasedOnScore]);

  const bestExperienceCheapestFare = useMemo(() => {
    startRecordingTripViewModelProcessing();
    if (!bestExperienceCheapestFareViewModel) return;

    const bestExperienceCheapestFare = convertFlightSearchResultFarePriceToPrice(
      bestExperienceCheapestFareViewModel.fare.price,
      passengerCount,
      priceOptions.showTotalPrice,
      true,
    );

    stopRecordingTripViewModelProcessing({
      index: pollIndex,
      name: 'bestExperienceCheapestFare',
    });

    return bestExperienceCheapestFare;
  }, [bestExperienceCheapestFareViewModel, passengerCount, priceOptions.showTotalPrice]);

  const multiCityActiveLeg = legFilterOptions.find((leg) => leg?.leg?.index === activeLeg);

  const allLegsAirlinesFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const allLegsAirlinesFilterOptions = isMultiCity
      ? uniqBy(
          legFilterOptions
            ?.map(
              (leg) =>
                leg?.leg?.airlines
                  .map(
                    (airline, index) =>
                      ({
                        id: index,
                        name: airline?.code,
                        price: airline?.price,
                        airline: airline?.airline,
                      } as FlightMetaSearchFilterOption),
                  )
                  .sort((a, b) => a.airline!.name?.localeCompare(b.airline!.name)) || [],
            )
            .flat(),
          'name',
        )
      : airlineFilterOptions;

    stopRecordingFilterProcessing({
      name: 'allLegsAirlinesFilterOptions',
      index: pollIndex,
    });

    return allLegsAirlinesFilterOptions;
  }, [airlineFilterOptions, isMultiCity, legFilterOptions]);
  const allLegsStopoverAirportFilterOptions = useMemo(() => {
    startRecordingFilterProcessing();
    const allLegsStopoverAirportFilterOptions = isMultiCity
      ? uniqBy(
          legFilterOptions
            ?.map(
              (leg) =>
                leg?.leg?.stopoverAirports.map(
                  (airport, index) =>
                    ({
                      id: index,
                      name: airport.code,
                      price: airport.price,
                      city: airport.city,
                      airport: airport.airport,
                    } as FlightMetaSearchFilterOption),
                ) || [],
            )
            .flat(),
          'name',
        )
      : stopoverAirportFilterOptions;

    stopRecordingFilterProcessing({
      name: 'allLegsStopoverAirportFilterOptions',
      index: pollIndex,
    });

    return allLegsStopoverAirportFilterOptions;
  }, [stopoverAirportFilterOptions, isMultiCity, legFilterOptions]);

  const usdPrices = useMemo(() => {
    const allPrices: Array<number> = [];

    tripViewModels.forEach((tripViewModel) => {
      const fareViewModel = tripViewModel.fares[0];

      if (fareViewModel) {
        const { amountUsd, totalAmountUsd } = fareViewModel.fare.price;

        let calculatedAmount = 0;

        if (priceOptions.showTotalPrice) {
          calculatedAmount = totalAmountUsd;
        } else {
          calculatedAmount = amountUsd;
        }

        allPrices.push(calculatedAmount);
      }
    });

    return allPrices;
  }, [priceOptions.showTotalPrice, tripViewModels]);

  const minUsdPrice = usdPrices.length ? Math.min(...usdPrices) : 0;

  const maxUsdPrice = usdPrices.length ? Math.max(...usdPrices) : 0;

  const isRenderingFirstResults =
    (!!sortedAndFilteredTripViewModels.length || !!groupedTripViewModelsDetail.length) &&
    !hasRenderedResults;

  const providerValue = useMemo(
    () =>
      ({
        search,
        isLoading,
        pollIndex,
        flightMetaSearchError,
        flightMetaSearchOriginalError,
        showTurnstileWidget,
        progressPercent,
        airlineFilterOptions,
        allianceFilterOptions,
        destinationAirportFilterOptions,
        originAirportFilterOptions,
        stopFilterOptions,
        stopoverAirportFilterOptions,
        legFilterOptions,
        stopoverDurationFilterOptions,
        tripViewModels,
        sponsoredAdTripViewModels,
        exchangeRates,
        providerFilterOptions,
        exchangeRate,
        expandedGroupKeys,
        setExpandedGroupKeys,
        setLeaderboardAdsAndInlineAdsConfig,
        groupedTripViewModelDetailWithoutPlaceholder,
        finalTripViewModelsToShowBasedOnSortType,
        sortedAndFilteredTripViewModelsForPerLegSearch,
        sortedAndFilteredTripViewModelsWithoutPlaceholder,
        sortedAndFilteredTripViewModelsForRecommendedSortFlight2,
        filteredSponsoredAdTripViewModels,
        groupedTripViewModelsDetail,
        sortedAndFilteredTripViewModels,
        cheapestFareViewModel,
        cheapestFare,
        fastestCheapestFare,
        bestExperienceCheapestFareViewModel,
        bestExperienceCheapestFare,
        aggregatedFlightSearchResult,
        multiCityActiveLeg,
        allLegsAirlinesFilterOptions,
        allLegsStopoverAirportFilterOptions,
        minUsdPrice,
        maxUsdPrice,
        usdPrices,
        hasRenderedResults,
        setHasRenderedResults,
        isRenderingFirstResults,
        offerEngineIntegrationAbTestVariant: getOfferEngineIntegrationAbTestVariant(search),
        isPollFailingMidway,
      } as FlightSearchResultsProviderValue),
    [
      search,
      isLoading,
      pollIndex,
      flightMetaSearchError,
      flightMetaSearchOriginalError,
      showTurnstileWidget,
      progressPercent,
      airlineFilterOptions,
      allianceFilterOptions,
      destinationAirportFilterOptions,
      originAirportFilterOptions,
      stopFilterOptions,
      stopoverAirportFilterOptions,
      legFilterOptions,
      stopoverDurationFilterOptions,
      tripViewModels,
      sponsoredAdTripViewModels,
      exchangeRates,
      providerFilterOptions,
      exchangeRate,
      expandedGroupKeys,
      groupedTripViewModelDetailWithoutPlaceholder,
      finalTripViewModelsToShowBasedOnSortType,
      sortedAndFilteredTripViewModelsWithoutPlaceholder,
      sortedAndFilteredTripViewModelsForRecommendedSortFlight2,
      filteredSponsoredAdTripViewModels,
      groupedTripViewModelsDetail,
      sortedAndFilteredTripViewModels,
      cheapestFareViewModel,
      cheapestFare,
      fastestCheapestFare,
      bestExperienceCheapestFareViewModel,
      bestExperienceCheapestFare,
      aggregatedFlightSearchResult,
      multiCityActiveLeg,
      allLegsAirlinesFilterOptions,
      allLegsStopoverAirportFilterOptions,
      minUsdPrice,
      maxUsdPrice,
      usdPrices,
      hasRenderedResults,
      setHasRenderedResults,
      isRenderingFirstResults,
      sortedAndFilteredTripViewModelsForPerLegSearch,
      isPollFailingMidway,
    ],
  );

  return (
    <FlightSearchResultsContext.Provider value={providerValue}>
      {children}
    </FlightSearchResultsContext.Provider>
  );
};

export default FlightSearchResultsProvider;

export const useFlightSearchResults = () => useContext(FlightSearchResultsContext);

const aggregateFlightSearchResults = (...flightSearchResults: FlightSearchResult[]) => {
  const mergedFlightSearchResult = flightSearchResults.reduce((acc, cur) => {
    return {
      airlines: [...cur.airlines, ...acc.airlines],
      airports: [...cur.airports, ...acc.airports],
      cities: [...cur.cities, ...acc.cities],
      fares: [...cur.fares, ...acc.fares],
      filters: cur.filters
        ? {
            airlines: [...cur.filters.airlines, ...acc.filters.airlines],
            alliances: [...cur.filters.alliances, ...acc.filters.alliances],
            destinationAirports: [
              ...cur.filters.destinationAirports,
              ...acc.filters.destinationAirports,
            ],
            originAirports: [...cur.filters.originAirports, ...acc.filters.originAirports],
            legs: [...cur.filters.legs, ...acc.filters.legs],
            stopoverAirports: [...cur.filters.stopoverAirports, ...acc.filters.stopoverAirports],
            stops: [...cur.filters.stops, ...acc.filters.stops],
            stopoverDurations: cur.filters.stopoverDurations,
          }
        : acc.filters,
      legs: [...cur.legs, ...acc.legs],
      paymentMethods: [...cur.paymentMethods, ...acc.paymentMethods],
      providers: [...cur.providers, ...acc.providers],
      trips: [...cur.trips, ...acc.trips],
      sponsors: cur.sponsors,
      search: cur.search,
      currencies: cur.currencies ?? acc.currencies,
      count: acc.count + cur.count,
      scores: { ...acc.scores, ...cur.scores },
    } as FlightSearchResult;
  }, EMPTY_FLIGHT_SEARCH_RESULT);

  const groupedLegFilters = groupBy(
    mergedFlightSearchResult.filters.legs.map((leg) => ({
      ...leg,
      id: leg.index,
      name: `${leg.departureCityCode}-${leg.arrivalCityCode}-${leg.index}`,
    })),
    'name',
  );

  const aggregatedLegFilters = Object.values(groupedLegFilters).map((legFilterGroup) => {
    const baseLegFilter = legFilterGroup[0];

    return {
      ...baseLegFilter,
      airlines: uniqBy(
        legFilterGroup.reduce((acc, cur) => {
          return [...cur.airlines, ...acc];
        }, [] as FlightSearchResultFiltersAirline[]),
        'code',
      ),
      stopoverAirports: uniqBy(
        legFilterGroup.reduce(
          (acc, cur) => [...cur.stopoverAirports, ...acc],
          [] as FlightSearchResultFiltersStopoverAirport[],
        ),
        'code',
      ),
      departureTimes: mergeMinMax(legFilterGroup.map((legFilter) => legFilter.departureTimes)),
      arrivalTimes: mergeMinMax(legFilterGroup.map((legFilter) => legFilter.arrivalTimes)),
      durations: mergeMinMax(legFilterGroup.map((legFilter) => legFilter.durations)),
      stopoverDurations: mergeMinMax(
        legFilterGroup.map((legFilter) => legFilter.stopoverDurations),
      ),
      stops: uniqBy(
        legFilterGroup.reduce(
          (acc, cur) => [...cur.stops, ...acc],
          [] as FlightSearchResultFiltersStop[],
        ),
        'code',
      ),
    };
  });

  return {
    airlines: uniqBy(mergedFlightSearchResult.airlines, 'code'),
    airports: uniqBy(mergedFlightSearchResult.airports, 'code'),
    cities: uniqBy(mergedFlightSearchResult.cities, 'code'),
    fares: mergedFlightSearchResult.fares,
    filters: {
      airlines: uniqBy(mergedFlightSearchResult.filters.airlines, 'code'),
      alliances: uniqBy(mergedFlightSearchResult.filters.alliances, 'code').filter(
        ({ code }) => code,
      ),
      destinationAirports: uniqBy(mergedFlightSearchResult.filters.destinationAirports, 'code'),
      originAirports: uniqBy(mergedFlightSearchResult.filters.originAirports, 'code'),
      stopoverAirports: uniqBy(mergedFlightSearchResult.filters.stopoverAirports, 'code'),
      legs: aggregatedLegFilters,
      stops: uniqBy(mergedFlightSearchResult.filters.stops, 'code'),
      stopoverDurations: mergedFlightSearchResult.filters.stopoverDurations,
    },
    legs: uniqBy(mergedFlightSearchResult.legs, 'id'),
    paymentMethods: uniqBy(mergedFlightSearchResult.paymentMethods, 'id').sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }

      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }

      return 0;
    }),
    providers: uniqBy(mergedFlightSearchResult.providers, 'code'),
    trips: uniqBy(mergedFlightSearchResult.trips, 'id'),
    search: mergedFlightSearchResult.search,
    sponsors: mergedFlightSearchResult.sponsors,
    currencies: uniqBy(mergedFlightSearchResult.currencies || [], 'code'),
    count: mergedFlightSearchResult.count,
    scores: mergedFlightSearchResult.scores,
  } as FlightSearchResult;
};
